import React, { useMemo } from 'react';
import { cotes, centroide, HALO } from '../../utils/cotes';

// ════════════════════════════════════════════════════════════════════════════
// Dossier de présentation Apex — A4 PAYSAGE, charte IDENTIQUE au deck St Maur.
// Mêmes classes/couleurs/typo que presentation/build_deck.py.
// Imprimable → PDF (window.print, @page A4 landscape). Données via sessionStorage.
// ════════════════════════════════════════════════════════════════════════════

const ORANGE = '#ea580c';

function fmt(n) { return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Math.round(n || 0)); }
function fmt1(n) { return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n || 0); }
function eur(n) { return `${fmt(n)} €`; }

// ── Géométrie (shoelace) ────────────────────────────────────────────────────
function polyArea(v) { let a = 0; for (let i = 0; i < v.length; i++) { const j = (i + 1) % v.length; a += v[i].x * v[j].y - v[j].x * v[i].y; } return Math.abs(a) / 2; }
function polyPerimeter(v) { let p = 0; for (let i = 0; i < v.length; i++) { const j = (i + 1) % v.length; p += Math.hypot(v[j].x - v[i].x, v[j].y - v[i].y); } return p; }
function polyBbox(v) { let a = Infinity, b = Infinity, c = -Infinity, d = -Infinity; for (const p of v) { a = Math.min(a, p.x); b = Math.min(b, p.y); c = Math.max(c, p.x); d = Math.max(d, p.y); } return { minX: a, minY: b, w: c - a, h: d - b }; }
function polyLen(pts) { if (!pts || pts.length < 2) return 0; let l = 0; for (let i = 0; i < pts.length - 1; i++) l += Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y); return l; }
const CLOISON_COLORS = { existing: '#475569', create: '#2563eb', demolish: '#dc2626' };
// L'ordre de lecture : les étages du bas vers le haut, puis les pièces sans
// étage (l'ensemble du bien en dernier) ; dans une pièce, les lots dans
// l'ordre du chantier.
const ORDRE_NIVEAUX = ['Sous-sol', 'Rez-de-chaussée', '1er étage', '2e étage', '3e étage', 'Combles', 'Extérieur'];
const rangNiveau = (n) => { const i = ORDRE_NIVEAUX.indexOf(n || ''); return i === -1 ? (n ? 50 : 99) : i; };
// Le niveau figure déjà à côté du nom : « Cuisine (Rez-de-ch) · Rez-de-chaussée » se lisait deux fois.
const nomCourt = (label) => String(label || '').replace(/\s*\([^)]*(rez|rdc|[ée]tage|sous-sol|combles|ext[ée]rieur)[^)]*\)\s*$/i, '').trim() || String(label || '');
// Un lieu vu sur les photos mais jamais coté n'a pas de vraie surface ; l'ensemble du bien n'est pas une pièce.
// Le drapeau posé par la page devis fait foi ; les anciens dossiers n'avaient que la note.
const estProvisoire = (r) => !r.technique && (r.provisoire !== undefined ? !!r.provisoire : /provisoire|non coté/i.test(r.notes || ''));
const HORS_LOGEMENT = /garage|cave|cellier|grenier|comble|local|chaufferie|cage|palier commun|fa[çc]ade|ext[ée]rieur/i;
const COMMUNS = 'Parties communes & bâtiment';
const estEnsemble = (r) => !!r.ensemble || /^ensemble du bien$/i.test(String(r.label || '').trim());
const ORDRE_LOTS = ['demolition', 'preparation', 'murs', 'sols', 'plomberie', 'electricite', 'chauffage', 'menuiseries', 'isolation', 'facade', 'divers', '_extras'];
const rangLot = (cat) => { const i = ORDRE_LOTS.indexOf(cat); return i === -1 ? 90 : i; };

// ── Logo + wordmark (charte deck) ───────────────────────────────────────────
const LogoSvg = ({ s = 40 }) => (
  <svg width={s} height={s} viewBox="0 0 46 46" fill="none" style={{ flexShrink: 0 }}>
    <rect width="46" height="46" rx="10" fill="#ea580c" />
    <path d="M23 8 L38 37 H32 L28.5 29 H17.5 L14 37 H8 Z" fill="white" />
    <rect x="19" y="23" width="10" height="4" fill="#ea580c" />
  </svg>
);
function Wordmark({ dark }) {
  return (
    <div className="wm">
      <LogoSvg />
      <div className="wm-t">
        <div className="wm-apex" style={{ color: dark ? '#fff' : '#0f172a' }}>APEX</div>
        <div className="wm-bar" />
        <div className="wm-sub" style={{ color: dark ? '#cbd5e1' : '#64748b' }}>Rénovation</div>
      </div>
    </div>
  );
}
function Hdr({ label, n }) {
  return (
    <div className="hdr">
      <Wordmark />
      <div className="hdr-r">
        <span className="hdr-label">{label}</span>
        <span className="hdr-pg">{String(n).padStart(2, '0')}</span>
      </div>
    </div>
  );
}

// ── Façade, toiture : pas de sol ────────────────────────────────────────────
// Élévation schématique et surface traitée (celle des postes), jamais
// Sol / Murs / Périmètre (cf. scripts/dossier-pdf.js).
const EXTERIEUR = /fa[çc]ade|ext[ée]rieur|toiture|pignon|ravalement/i;
const estExterieur = (r) => r?.type === 'facade' || EXTERIEUR.test(r?.label || '');
function Elevation({ surface, toiture }) {
  return (
    <div>
      <svg viewBox="0 0 300 140" width="100%" style={{ border: '1px solid #e2e8f0', borderRadius: 10, background: '#fafafa', display: 'block' }}>
        {toiture
          ? <path d="M70,104 L150,40 L230,104 Z" fill="#fff7ed" stroke={ORANGE} strokeWidth="1.5" strokeLinejoin="round" />
          : <>
              <rect x="80" y="34" width="140" height="84" fill="#fff7ed" stroke={ORANGE} strokeWidth="1.5" />
              {[[96, 46], [136, 46], [176, 46], [96, 82], [176, 82]].map(([x, y]) => <rect key={`${x}-${y}`} x={x} y={y} width="22" height="22" fill="#fff" stroke="#fdba74" />)}
              <rect x="137" y="88" width="24" height="30" fill="#fff" stroke="#fdba74" />
            </>}
        <line x1="40" y1="118" x2="260" y2="118" stroke="#94a3b8" strokeWidth="1.5" />
        {surface > 0 && <text x="150" y="20" textAnchor="middle" fill={ORANGE} fontSize="13" fontWeight="900">{surface.toFixed(1)} m²</text>}
      </svg>
      <div className="planmeta">
        <span><b>{toiture ? 'Toiture' : 'Façade'}</b> {surface > 0 ? `${surface.toFixed(1)} m² traités` : 'surface à relever'}</span>
        <span>Élévation schématique, hors échelle</span>
      </div>
    </div>
  );
}

// ── Plan SVG d'une pièce ────────────────────────────────────────────────────
function RoomPlan({ vertices, height, walls = [] }) {
  const h = parseFloat(height) || 2.5;
  if (!vertices || vertices.length < 3) return null;
  const floorArea = polyArea(vertices), perimeter = polyPerimeter(vertices), wallArea = perimeter * h;
  const bb = polyBbox(vertices);
  const cC = (walls || []).filter(w => w.kind === 'create').reduce((s, w) => s + polyLen(w.points), 0) * h;
  const cD = (walls || []).filter(w => w.kind === 'demolish').reduce((s, w) => s + polyLen(w.points), 0) * h;
  // Marges : la place des cotes, posées hors du contour (une cote latérale
  // fait ~30 px de large à cette taille).
  const svgW = 300, svgH = 140, padX = 38, padY = 20;
  const scale = Math.min((svgW - padX * 2) / Math.max(bb.w, 0.1), (svgH - padY * 2) / Math.max(bb.h, 0.1));
  const offX = (svgW - bb.w * scale) / 2 - bb.minX * scale, offY = (svgH - bb.h * scale) / 2 - bb.minY * scale;
  const toSvg = v => ({ x: v.x * scale + offX, y: v.y * scale + offY });
  const pathD = vertices.map((v, i) => { const s = toSvg(v); return `${i === 0 ? 'M' : 'L'}${s.x.toFixed(1)},${s.y.toFixed(1)}`; }).join(' ') + ' Z';
  const center = toSvg(centroide(vertices));
  const cs = cotes(vertices, toSvg, { ecart: 7, minLen: 0.2, minPx: 14 });
  return (
    <div>
      <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" style={{ border: '1px solid #e2e8f0', borderRadius: 10, background: '#fafafa', display: 'block' }}>
        <path d={pathD} fill="#fff7ed" stroke={ORANGE} strokeWidth="1.5" strokeLinejoin="round" />
        {cs.map(c => <text key={c.i} x={c.x} y={c.y} textAnchor={c.anchor} dominantBaseline={c.baseline} fill="#475569" fontSize="8" fontWeight="700" {...HALO} strokeWidth={2.5}>{c.len.toFixed(2)} m</text>)}
        <text x={center.x} y={center.y - 3} textAnchor="middle" fill={ORANGE} fontSize="14" fontWeight="900" {...HALO}>{floorArea.toFixed(1)} m²</text>
        <text x={center.x} y={center.y + 10} textAnchor="middle" fill="#94a3b8" fontSize="8" {...HALO} strokeWidth={2}>h = {h.toFixed(1)} m</text>
        {(walls || []).map((w, wi) => { const color = CLOISON_COLORS[w.kind] || CLOISON_COLORS.create; const d = (w.points || []).map((p, i) => { const s = toSvg(p); return `${i === 0 ? 'M' : 'L'}${s.x.toFixed(1)},${s.y.toFixed(1)}`; }).join(' '); return <path key={w.id ?? wi} d={d} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray={w.kind === 'demolish' ? '4,3' : undefined} />; })}
      </svg>
      <div className="planmeta">
        <span><b>Sol</b> {floorArea.toFixed(1)} m²</span>
        <span><b>Murs</b> {wallArea.toFixed(1)} m²</span>
        <span><b>Périmètre</b> {perimeter.toFixed(1)} ml</span>
        {cC > 0.05 && <span style={{ color: '#2563eb' }}><b>Cloison créée</b> {cC.toFixed(1)} m²</span>}
        {cD > 0.05 && <span style={{ color: '#dc2626' }}><b>Cloison démolie</b> {cD.toFixed(1)} m²</span>}
      </div>
    </div>
  );
}

// ── Matériaux dérivés des travaux ───────────────────────────────────────────
function deriveMaterials(allKeys, style = {}) {
  // La teinte choisie à l'étape Style, sinon blanc mat.
  const teinte = style?.wallColor?.label || 'Blanc mat';
  const sol = style?.floorTone?.label;
  const keys = allKeys.join(' ');
  const mats = [];
  if (/sols\.(sol_pvc)/.test(keys)) mats.push({ icon: '🪵', label: 'Sol PVC/vinyle effet parquet', detail: `${sol ? `Teinte ${sol.toLowerCase()}` : 'Lames effet bois'} qualité pro, pose collée` });
  else if (/sols\.parquet/.test(keys)) mats.push({ icon: '🪵', label: 'Parquet stratifié', detail: 'Sous-couche acoustique + pose flottante' });
  if (/sols\.carrelage/.test(keys)) mats.push({ icon: '◻️', label: 'Carrelage grès cérame', detail: 'Grand format, pose + joints' });
  if (/murs\.peinture_murs/.test(keys)) mats.push({ icon: '🎨', label: 'Peinture murs', detail: `${teinte} — sous-couche + 2 couches` });
  if (/murs\.peinture_plafond/.test(keys)) mats.push({ icon: '⬜', label: 'Peinture plafond', detail: 'Blanc mat — sous-couche + 2 couches' });
  if (/murs\.(cloison_placo)/.test(keys)) mats.push({ icon: '🧱', label: 'Cloisons placo BA13', detail: 'Ossature métallique + double parement' });
  if (/(murs|demolition)\.(plafond_suspendu)/.test(keys)) mats.push({ icon: '▦', label: 'Faux-plafond dalles 60×60', detail: 'Ossature T24 + dalles, plénum repris' });
  if (/electricite\.(spot|panneau|point)/.test(keys)) mats.push({ icon: '💡', label: 'Éclairage LED', detail: 'Spots / panneaux encastrés basse conso' });
  if (/menuiseries\.porte/.test(keys)) mats.push({ icon: '🚪', label: 'Menuiseries intérieures', detail: 'Portes âme pleine + quincaillerie' });
  if (/menuiseries\.banque/.test(keys)) mats.push({ icon: '🛎️', label: 'Banque d\'accueil', detail: 'Agencement sur mesure, fourniture + pose' });
  if (/electricite\.vmc|chauffage\./.test(keys)) mats.push({ icon: '🌀', label: 'Ventilation & chauffage', detail: 'VMC + émetteurs selon l\'existant' });
  if (/plomberie\./.test(keys)) mats.push({ icon: '🚿', label: 'Sanitaires & plomberie', detail: 'Appareils + robinetterie qualité pro' });
  return mats;
}

const STEPS = [
  { n: 1, t: 'Relevé & validation', d: 'Métré contradictoire sur site, validation du périmètre des travaux.' },
  { n: 2, t: 'Devis signable', d: 'Émission du devis contractuel détaillé à signer.' },
  { n: 3, t: 'Planning', d: 'Calage des dates et coordination des corps de métier.' },
  { n: 4, t: 'Chantier', d: 'Réalisation suivie par Apex, interlocuteur unique, réception.' },
];

const CSS = `
  table.devis tr.grp td{background:#0f172a;color:#fff;font-weight:800;font-size:10.5pt;padding:7px 8px;letter-spacing:.2px}
  table.devis tr.grp td span{font-weight:400;color:#94a3b8;font-size:9pt}
  table.devis tr.grp td.num{color:#fdba74}
@page { size: A4 landscape; margin: 0; }
.deck-root * { margin:0; padding:0; box-sizing:border-box; }
.deck-root{ --o:#ea580c; --o2:#c2410c; --ink:#0f172a; --slate:#475569; --mute:#94a3b8; --line:#e2e8f0; --panel:#f8fafc; --soft:#fff7ed;
  font-family:'Inter',-apple-system,'Segoe UI',sans-serif; color:var(--ink); -webkit-print-color-adjust:exact; print-color-adjust:exact; }
.deck-root .page{ position:relative; width:297mm; height:210mm; padding:15mm 17mm; overflow:hidden; background:#fff; }
.deck-root .kicker{ color:var(--o); font-size:10.5pt; font-weight:700; letter-spacing:2.5px; text-transform:uppercase; margin-bottom:5px; }
.deck-root .kicker.light{ color:#fdba74; }
.deck-root h1{ font-size:34pt; font-weight:800; line-height:1.04; letter-spacing:-1px; }
.deck-root h2{ font-size:20pt; font-weight:800; letter-spacing:-.5px; margin-bottom:10px; text-wrap:balance; }
.deck-root p{ font-size:11pt; line-height:1.55; color:var(--slate); }
.deck-root .dot{ color:var(--o); padding:0 4px; }
.deck-root .wm{ display:inline-flex; align-items:center; gap:11px; }
.deck-root .wm-t{ display:flex; flex-direction:column; }
.deck-root .wm-apex{ font-weight:900; font-size:15pt; line-height:1; letter-spacing:2px; }
.deck-root .wm-bar{ width:100%; height:2px; background:var(--o); margin:3px 0 2px; }
.deck-root .wm-sub{ font-weight:300; font-size:7.5pt; letter-spacing:2.5px; text-transform:uppercase; }
.deck-root .hdr{ display:flex; justify-content:space-between; align-items:center; padding-bottom:8px; border-bottom:1.5px solid var(--line); margin-bottom:12px; }
.deck-root .hdr-r{ display:flex; align-items:center; gap:14px; }
.deck-root .hdr-label{ color:var(--slate); font-size:9.5pt; font-weight:600; letter-spacing:1px; text-transform:uppercase; }
.deck-root .hdr-pg{ color:#fff; background:var(--o); font-size:9pt; font-weight:800; width:26px; height:26px; border-radius:7px; display:inline-flex; align-items:center; justify-content:center; }
.deck-root .cover{ padding:0; color:#fff; }
.deck-root .cover-bg{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; }
.deck-root .cover-grad{ background:linear-gradient(135deg,#0f172a 0%,#1e293b 55%,#334155 100%); }
.deck-root .cover-veil{ position:absolute; inset:0; background:linear-gradient(180deg, rgba(15,23,42,.55) 0%, rgba(15,23,42,.18) 34%, rgba(15,23,42,.45) 64%, rgba(15,23,42,.95) 100%); }
.deck-root .cover-top{ position:absolute; top:15mm; left:17mm; }
.deck-root .cover-body{ position:absolute; left:17mm; bottom:30mm; max-width:230mm; }
.deck-root .cover h1{ margin:6px 0 14px; }
.deck-root .cover-sub{ font-size:16pt; font-weight:600; color:#fff; }
.deck-root .cover-line{ width:64px; height:4px; background:var(--o); border-radius:3px; margin:18px 0 12px; }
.deck-root .cover-meta{ font-size:11pt; color:#cbd5e1; }
.deck-root .cover-foot{ position:absolute; left:17mm; bottom:13mm; font-size:8.5pt; color:#94a3b8; letter-spacing:.5px; }
.deck-root .intro{ font-size:10pt; line-height:1.5; color:var(--slate); margin-bottom:12px; max-width:240mm; }
.deck-root .callout{ background:var(--panel); border-left:3px solid var(--o); border-radius:0 8px 8px 0; padding:12px 15px; font-size:10pt; line-height:1.5; color:var(--slate); margin-top:8px; }
/* room plans */
.deck-root .roomgrid{ display:grid; grid-template-columns:repeat(3,1fr); gap:12px; }
.deck-root .roomcard{ border:1px solid var(--line); border-radius:12px; padding:10px 12px; }
.deck-root .roomcard-h{ display:flex; justify-content:space-between; align-items:baseline; gap:8px; margin-bottom:6px; }
.deck-root .roomcard-h b{ font-size:10pt; line-height:1.25; } .deck-root .roomcard-h span{ font-size:9pt; color:var(--mute); white-space:nowrap; }
.deck-root .planmeta{ display:flex; gap:10px; margin-top:4px; font-size:8pt; color:#64748b; flex-wrap:wrap; }
.deck-root .planvide{ font-size:8.5pt; color:var(--mute); line-height:1.45; border:1px dashed var(--line); border-radius:10px; padding:14px 12px; min-height:34mm; display:flex; align-items:center; }
.deck-root .planmeta b{ color:var(--o2); font-weight:700; }
/* before/after */
.deck-root .ba{ display:grid; grid-template-columns:1fr 1fr; gap:16px; height:98mm; }
.deck-root .ba figure{ position:relative; height:100%; border-radius:12px; overflow:hidden; border:1px solid var(--line); }
.deck-root .ba img{ width:100%; height:100%; object-fit:cover; }
.deck-root .ba figcaption{ position:absolute; top:12px; left:12px; }
.deck-root .badge{ font-size:9pt; font-weight:800; letter-spacing:.5px; padding:6px 13px; border-radius:20px; color:#fff; box-shadow:0 4px 14px rgba(0,0,0,.25); }
.deck-root .badge.grey{ background:rgba(15,23,42,.82); } .deck-root .badge.orange{ background:var(--o); }
.deck-root .ba-cap{ margin-top:10px; font-size:9.5pt; line-height:1.45; color:var(--slate); background:var(--panel); border-radius:8px; padding:9px 14px; }
.deck-root .ba-cap p{ font-size:9.5pt; line-height:1.45; display:-webkit-box; -webkit-line-clamp:5; -webkit-box-orient:vertical; overflow:hidden; }
/* répartition par corps de métier (page budget) */
.deck-root .lots{ margin-top:14px; }
.deck-root .lots-t{ font-size:9pt; color:var(--mute); font-weight:600; text-transform:uppercase; letter-spacing:1px; margin-bottom:6px; }
.deck-root .lots-g{ display:grid; grid-template-columns:repeat(3,1fr); gap:2px 18px; }
.deck-root .lot-r{ display:flex; align-items:baseline; gap:8px; font-size:9pt; border-bottom:1px solid var(--line); padding:3px 0; }
.deck-root .lot-r span{ flex:1; color:var(--slate); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.deck-root .lot-r b{ font-variant-numeric:tabular-nums; color:var(--ink); }
.deck-root .lot-r em{ font-style:normal; color:var(--mute); font-size:8pt; width:30px; text-align:right; }
/* materiaux */
.deck-root .matgrid{ display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-top:8px; }
.deck-root .matcard{ display:flex; gap:12px; align-items:flex-start; background:var(--panel); border-radius:12px; padding:12px 14px; }
.deck-root .matic{ font-size:20pt; line-height:1; }
.deck-root .matcard b{ font-size:10.5pt; display:block; } .deck-root .matcard span{ font-size:9pt; color:var(--slate); line-height:1.4; }
/* devis */
.deck-root .devis{ width:100%; border-collapse:collapse; font-size:8.5pt; table-layout:fixed; }
.deck-root .devis th{ text-align:left; color:var(--mute); font-size:8pt; text-transform:uppercase; letter-spacing:1px; padding:0 8px 6px; border-bottom:2px solid var(--ink); }
.deck-root .devis td{ padding:2px 6px; vertical-align:baseline; line-height:1.3; }
.deck-root .devis .num{ text-align:right; white-space:nowrap; font-variant-numeric:tabular-nums; width:22mm; }
.deck-root .devis th.u, .deck-root .devis td.u{ text-align:center; white-space:nowrap; color:var(--mute); font-size:8pt; width:14mm; }
.deck-root .devis th.num{ width:22mm; }
.deck-root .devis td.qte, .deck-root .devis td.pu{ color:var(--slate); }
.deck-root .devis td.tot{ color:var(--ink); font-weight:600; }
.deck-root .devis tr.lot td{ background:var(--soft); color:var(--o2); font-weight:800; font-size:8.5pt; padding-top:4px; padding-bottom:4px; border-top:1px solid #fed7aa; }
.deck-root .devis td.d{ color:var(--ink); font-weight:500; }
.deck-root .devis tbody tr:not(.lot){ border-bottom:1px solid var(--line); }
.deck-root .devis tfoot .grand td{ padding-top:8px; font-weight:800; font-size:11.5pt; color:var(--ink); border-top:2px solid var(--ink); }
.deck-root .devis tfoot .grand .num{ color:var(--o); }
.deck-root .page.devispage h2{ margin-bottom:9px; }
.deck-root .mini{ font-size:8.5pt; color:var(--mute); margin-top:12px; line-height:1.4; }
/* budget */
.deck-root .budget{ display:flex; align-items:stretch; gap:12px; margin:18px 0 14px; }
.deck-root .bcard{ flex:1; border:1px solid var(--line); border-radius:12px; padding:16px 18px; display:flex; flex-direction:column; }
.deck-root .bcard.hl{ background:var(--ink); border-color:var(--ink); color:#fff; flex:1.3; }
.deck-root .bk{ font-size:9.5pt; color:var(--mute); font-weight:600; text-transform:uppercase; letter-spacing:1px; }
.deck-root .bcard.hl .bk{ color:#cbd5e1; }
.deck-root .bv{ font-size:22pt; font-weight:800; letter-spacing:-.5px; margin-top:4px; }
.deck-root .bcard.hl .bv{ color:#fff; } .deck-root .bcard:not(.hl) .bv{ color:var(--ink); }
.deck-root .bu{ font-size:9pt; color:var(--mute); font-weight:600; }
.deck-root .bplus,.deck-root .beq{ display:flex; align-items:center; font-size:20pt; color:var(--o); font-weight:800; }
.deck-root .budget2{ display:grid; grid-template-columns:repeat(3,1fr); gap:12px; }
.deck-root .b2{ background:var(--panel); border-radius:10px; padding:13px 16px; display:flex; flex-direction:column; gap:3px; }
.deck-root .b2 span{ font-size:9pt; color:var(--slate); } .deck-root .b2 b{ font-size:13pt; color:var(--ink); }
.deck-root .b2.ttc{ background:var(--o); } .deck-root .b2.ttc span{ color:#ffe6d5; } .deck-root .b2.ttc b{ color:#fff; font-size:15pt; }
/* steps */
.deck-root .steps{ display:grid; grid-template-columns:repeat(2,1fr); gap:14px; margin-bottom:20px; }
.deck-root .step{ display:flex; gap:14px; align-items:flex-start; border:1px solid var(--line); border-radius:12px; padding:15px 17px; }
.deck-root .sn{ width:34px; height:34px; border-radius:9px; background:var(--soft); color:var(--o); font-weight:800; font-size:14pt; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.deck-root .step b{ font-size:11pt; display:block; margin-bottom:2px; } .deck-root .step span{ font-size:9.5pt; color:var(--slate); line-height:1.4; }
.deck-root .contact{ display:flex; justify-content:space-between; align-items:center; background:var(--panel); border-radius:13px; padding:18px 22px; }
.deck-root .contact-r{ display:flex; gap:34px; }
.deck-root .ci{ display:flex; flex-direction:column; }
.deck-root .ci span{ font-size:8.5pt; color:var(--mute); text-transform:uppercase; letter-spacing:1px; }
.deck-root .ci b{ font-size:11pt; color:var(--ink); margin-top:2px; }
.deck-root .finenote{ font-size:8pt; color:var(--mute); margin-top:16px; line-height:1.4; }
/* écran (aperçu) vs impression */
@media screen {
  body{ background:#1e293b; }
  .deck-root{ display:flex; flex-direction:column; align-items:center; gap:18px; padding:24px 0 90px; }
  .deck-root .page{ box-shadow:0 14px 44px rgba(0,0,0,.4); border-radius:2px; }
}
@media print {
  .deck-root .page{ page-break-after:always; }
  .deck-root .page:last-child{ page-break-after:auto; }
  .no-print{ display:none !important; }
}
.no-print.bar{ position:fixed; top:16px; right:16px; z-index:99; display:flex; gap:8px; }
.no-print.bar button{ border:none; cursor:pointer; padding:10px 20px; border-radius:10px; font-weight:700; font-size:14px; }
`;

export default function ProDossier() {
  // localStorage d'abord (fiable entre onglets, y compris Safari iPad),
  // sessionStorage en repli pour les anciens onglets
  const data = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('pro_dossier') || sessionStorage.getItem('pro_dossier')); } catch { return null; }
  }, []);

  // Le devis pièce par pièce, dans l'ordre des étages : c'est ainsi que le
  // client lit son projet (« ma cuisine coûte tant »). Dans une pièce, les
  // lignes suivent l'ordre des lots ; les prestations complémentaires ferment.
  const pieces = useMemo(() => {
    if (!data) return [];
    const rooms = (data.rooms || []).map((r, i) => ({ ...r, n: i + 1 })).sort((a, b) => rangNiveau(a.niveau) - rangNiveau(b.niveau) || a.n - b.n);
    const liste = rooms.map(r => {
      const lignes = (r.lines || []).map(l => {
        const qty = Number(l.qty) || 0, total = Number(l.total) || 0;
        return { cat: String(l.key || 'divers').split('.')[0], label: l.label, unit: l.unit, qty, total, pu: qty > 0 ? total / qty : Number(l.pu) || 0 };
      }).filter(l => l.total || l.qty).sort((a, b) => rangLot(a.cat) - rangLot(b.cat));
      const extras = (r.addons || []).map(a => {
        const qty = Number(a.qty) || 0, total = Number(a.total) || 0;
        return { cat: '_extras', label: a.label || 'Prestation complémentaire', unit: a.unit, qty, total, pu: qty > 0 ? total / qty : 0 };
      }).filter(l => l.total || l.qty);
      const lines = [...lignes, ...extras];
      return { id: r.id ?? r.n, n: r.n, label: r.label, type: r.type || null, niveau: r.niveau, surface: Number(r.surface) || 0, provisoire: estProvisoire(r), ensemble: estEnsemble(r), technique: r.technique || null, lines, subtotal: lines.reduce((t, l) => t + l.total, 0) };
    }).filter(p => p.lines.length);
    // Plusieurs logements : logement par logement (ses installations techniques,
    // puis ses pièces), puis les parties communes — comme le PDF du dossier.
    const logements = data.logements || [];
    // Numéros = pièces réelles seulement, dans l'ordre affiché (cf. dossier-pdf).
    const numeroter = (l) => { let k = 0; l.forEach(p => { p.n = (p.technique || p.ensemble) ? null : ++k; }); return l; };
    if (logements.length < 2) return numeroter(liste);
    const groupeDe = (p) => {
      if (p.technique) return p.technique;
      if (p.ensemble || HORS_LOGEMENT.test(p.label || '')) return COMMUNS;
      return logements.find(l => (l.niveaux || []).includes(p.niveau))?.nom || COMMUNS;
    };
    const ordre = [...logements.map(l => l.nom), COMMUNS];
    liste.forEach(p => { p.groupe = groupeDe(p); });
    liste.sort((a, b) => ordre.indexOf(a.groupe) - ordre.indexOf(b.groupe) || (b.technique ? 1 : 0) - (a.technique ? 1 : 0) || a.n - b.n);
    const vus = new Set();
    liste.forEach((p, i) => {
      p.n = i + 1;
      if (vus.has(p.groupe)) return;
      vus.add(p.groupe);
      const lg = logements.find(l => l.nom === p.groupe);
      p.entete = { titre: p.groupe, sous: lg ? `${(lg.niveaux || []).join(' + ')} · ${fmt1(lg.surface)} m² habitables` : 'cave, combles, façade, réseaux communs', total: liste.filter(x => x.groupe === p.groupe).reduce((t, x) => t + x.subtotal, 0) };
    });
    return numeroter(liste);
  }, [data]);

  // Répartition par corps de métier, pour la page budget.
  const lots = useMemo(() => {
    const map = new Map();
    for (const p of pieces) for (const l of p.lines) map.set(l.cat, (map.get(l.cat) || 0) + l.total);
    const total = [...map.values()].reduce((t, v) => t + v, 0) || 1;
    return [...map].map(([cat, montant]) => ({ cat, label: cat === '_extras' ? 'Prestations complémentaires' : (data?.catLabels?.[cat] || cat), montant, part: montant / total }))
      .filter(l => l.montant > 0).sort((a, b) => b.montant - a.montant);
  }, [pieces, data]);

  // Pagination du devis : ~24 lignes par page paysage à cette taille (16
  // laissait la moitié de la page vide), un libellé long (qui passe à la
  // ligne) comptant double. Une pièce trop longue pour une page se coupe,
  // avec son en-tête répété.
  const devisPages = useMemo(() => {
    const MAX = 24;
    const poids = (l) => (String(l.label || '').length > 80 ? 2 : 1);
    const pages = []; let cur = [], curLines = 0;
    for (const piece of pieces) {
      let reste = [...piece.lines];
      while (reste.length) {
        const tete = piece.entete && reste.length === piece.lines.length ? 1 : 0;
        if (curLines + 1 + tete + poids(reste[0]) > MAX && cur.length) { pages.push(cur); cur = []; curLines = 0; }
        const morceau = []; let ln = 1 + tete;
        while (reste.length && curLines + ln + poids(reste[0]) <= MAX) { const l = reste.shift(); morceau.push(l); ln += poids(l); }
        if (!morceau.length) { const l = reste.shift(); morceau.push(l); ln += poids(l); }
        cur.push({ ...piece, lines: morceau, suite: cur.some(x => x.id === piece.id) || pages.some(pg => pg.some(x => x.id === piece.id)) });
        curLines += ln;
      }
    }
    if (cur.length) pages.push(cur);
    return pages;
  }, [pieces]);

  if (!data) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontFamily: 'Inter, Arial, sans-serif' }}>
        <p style={{ fontSize: 18 }}>Aucun dossier à afficher.</p>
        <p style={{ fontSize: 13, marginTop: 8 }}>Générez-le depuis l'interface Pro Devis.</p>
      </div>
    );
  }

  const { client = {}, rooms = [], estimate, tvaLabel, state, date, projectPhotos = [], titre = '' } = data;
  // Six plans par page, dans l'ordre des étages ; l'ensemble du bien n'y figure pas.
  const roomsTriees = rooms.map((r, i) => ({ ...r, n: i + 1 })).filter(r => !estEnsemble(r) && !r.technique).sort((a, b) => rangNiveau(a.niveau) - rangNiveau(b.niveau) || a.n - b.n);
  // Même ordre que le devis : logement par logement, puis parties communes.
  const ordreDevis = new Map(pieces.map((p, i) => [p.label, i]));
  const numeroDe = new Map(pieces.map(p => [p.label, p.n]));
  // Surface traitée d'un extérieur = la plus grande quantité au m² de ses postes façade / ITE / toiture.
  const surfaceExt = (room) => Math.max(0, ...((pieces.find(p => p.label === room.label)?.lines) || [])
    .filter(l => l.unit === 'm²' && (/^(facade|ite|toiture)$/.test(l.cat) || /toiture/i.test(l.label || ''))).map(l => l.qty));
  roomsTriees.forEach(r => { r.n = numeroDe.get(r.label) ?? r.n; });
  const groupeDe = new Map(pieces.map(p => [p.label, p.groupe || '']));
  roomsTriees.sort((a, b) => (ordreDevis.get(a.label) ?? 999) - (ordreDevis.get(b.label) ?? 999));
  const planPages = [];
  for (const g of [...new Set(roomsTriees.map(r => groupeDe.get(r.label) || ''))]) {
    const rs = roomsTriees.filter(r => (groupeDe.get(r.label) || '') === g);
    for (let i = 0; i < rs.length; i += 6) planPages.push(Object.assign(rs.slice(i, i + 6), { groupe: g, suite: i > 0 }));
  }
  const allKeys = rooms.flatMap(r => (r.lines || []).map(l => String(l.key || '')));
  const materials = deriveMaterials(allKeys, data.style);
  // Un rendu dont la photo source a quitté la pièce reste montré (avec la première photo en « avant »).
  const afterRooms = rooms.filter(r => r.afterPhoto && r.afterPhoto.url && (r.photos || []).length)
    .sort((a, b) => (ordreDevis.get(a.label) ?? 999) - (ordreDevis.get(b.label) ?? 999));
  // Surface du projet : les pièces mesurées (ou supposées, comme une cave) — jamais un 3 × 3 provisoire.
  const nbPieces = roomsTriees.length;
  const nbProvisoires = roomsTriees.filter(estProvisoire).length;
  const totalSurface = roomsTriees.filter(r => !estProvisoire(r) && !estExterieur(r)).reduce((s, r) => s + (Number(r.surface) || 0), 0);
  const hero = afterRooms[0] || null;
  const coverImg = (hero && hero.afterPhoto.url) || projectPhotos[0]?.url || projectPhotos[0]?.src || null;
  const ttcLow = estimate.ttc * 0.92, ttcHigh = estimate.ttc * 1.08;

  let pn = 1; const next = () => (++pn); // couverture = 01

  // La légende dit ce que le rendu montre, d'après les postes de la pièce. Une
  // note courte du métreur s'y ajoute ; une longue (le résumé de l'analyse y
  // atterrit) ne l'écrase plus : elle débordait sous la page.
  const baCaption = (room) => {
    const keys = (room.lines || []).map(l => String(l.key || '')).join(' ');
    const bits = [];
    if (/sols\.renovation_parquet/.test(keys)) bits.push('parquet d\'origine poncé et vitrifié');
    else if (/sols\.dalle_beton_cave/.test(keys)) bits.push('dalle béton neuve');
    else if (/sols\.(parquet_stratifie|parquet_massif)/.test(keys)) bits.push('parquet neuf');
    else if (/sols\.sol_pvc/.test(keys)) bits.push('sol vinyle effet parquet');
    else if (/sols\.carrelage/.test(keys)) bits.push('carrelage neuf');
    if (/preparation\.decollage_papier/.test(keys)) bits.push('papier peint déposé');
    if (/murs\.peinture/.test(keys)) bits.push('murs repris et peints');
    if (/cloison/.test(keys)) bits.push('cloisons neuves');
    if (/menuiseries\.cuisine_complete/.test(keys)) bits.push('cuisine neuve');
    if (/plomberie\.(douche|baignoire|meuble|wc)/.test(keys)) bits.push('sanitaires neufs');
    if (/menuiseries\.fenetre/.test(keys)) bits.push('fenêtres neuves');
    if (/electricite\.(panneau|spot|point)/.test(keys)) bits.push('éclairage neuf');
    const base = bits.length ? `Projection photoréaliste après travaux — ${bits.join(' · ')}.` : 'Projection photoréaliste du résultat après travaux.';
    const note = String(room.notes || '').replace(/\s+/g, ' ').trim();
    return note && note.length <= 240 ? `${base} ${note}` : base;
  };

  return (
    <div className="deck-root">
      <style>{CSS}</style>
      <div className="no-print bar">
        <button onClick={() => window.print()} style={{ background: ORANGE, color: '#fff' }}>Imprimer / PDF</button>
        <button onClick={() => window.close()} style={{ background: '#e2e8f0', color: '#475569' }}>Fermer</button>
      </div>

      {/* ── 01 COUVERTURE ── */}
      <section className="page cover">
        {coverImg ? <img className="cover-bg" src={coverImg} alt="" /> : <div className="cover-bg cover-grad" />}
        <div className="cover-veil" />
        <div className="cover-top"><Wordmark dark /></div>
        <div className="cover-body">
          <div className="kicker light">Proposition de rénovation</div>
          <h1>{titre || <>Projet de<br />rénovation</>}</h1>
          {(client.prenom || client.nom) && <div className="cover-sub">{client.prenom} {client.nom}</div>}
          <div className="cover-line" />
          <div className="cover-meta">{nbPieces} pièce{nbPieces > 1 ? 's' : ''} <span className="dot">·</span> {fmt1(totalSurface)} m² <span className="dot">·</span> établi le {date}{client.adresse ? <> <span className="dot">·</span> {client.adresse}</> : null}</div>
          {(client.siege || client.siren || client.tvaIntra) && (
            <div className="cover-meta" style={{ marginTop: 6, opacity: 0.85 }}>
              {[client.siege && `Siège : ${client.siege}`, client.siren && `SIREN ${client.siren}`, client.tvaIntra && `TVA ${client.tvaIntra}`].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
        <div className="cover-foot">Apex Rénovation <span className="dot">·</span> Châteauroux &amp; l'Indre <span className="dot">·</span> Document de présentation</div>
      </section>

      {/* ── 02 PIÈCES & PLANS — six par page, étage par étage ── */}
      {planPages.map((pageRooms, pi) => {
        const niveaux = [...new Set(pageRooms.map(r => r.niveau).filter(Boolean))];
        return (
          <section className="page" key={`plans-${pi}`}>
            <Hdr label={planPages.length > 1 ? `Le projet · ${pi + 1}/${planPages.length}` : 'Le projet'} n={next()} />
            <div className="kicker">Relevé sur site{niveaux.length ? ` · ${niveaux.join(' · ')}` : ''}</div>
            <h2>{pageRooms.groupe ? `${pageRooms.groupe}${pageRooms.suite ? ' (suite)' : ''}` : (pi === 0 ? 'Pièces & plans' : 'Pièces & plans (suite)')}</h2>
            {pi === 0 && (
              <p className="intro">État général constaté : <b style={{ color: '#0f172a' }}>{state}</b>. Le projet porte sur {nbPieces} pièce{nbPieces > 1 ? 's' : ''} pour {fmt1(totalSurface)} m² relevés sur site avec géométrie, métré et photos{nbProvisoires ? ` — ${nbProvisoires} lieu${nbProvisoires > 1 ? 'x' : ''} vu${nbProvisoires > 1 ? 's' : ''} sur les photos reste${nbProvisoires > 1 ? 'nt' : ''} à coter au métré` : ''}.</p>
            )}
            <div className="roomgrid">
              {pageRooms.map((room) => (
                <div className="roomcard" key={room.id ?? room.label}>
                  <div className="roomcard-h"><b>{room.n}. {nomCourt(room.label)}{room.niveau ? <span style={{ fontWeight: 400, color: '#94a3b8' }}> · {room.niveau}</span> : null}</b><span>{estExterieur(room) ? (surfaceExt(room) ? `${fmt1(surfaceExt(room))} m²` : 'à relever') : estProvisoire(room) ? 'à relever' : `${fmt1(room.surface)} m²`}</span></div>
                  {estExterieur(room)
                    ? <Elevation surface={surfaceExt(room)} toiture={/toiture/i.test(room.label || '')} />
                    : estProvisoire(room)
                    ? <div className="planvide">Lieu vu sur les photos, non coté pendant la visite : surface et plan à relever au métré contradictoire.</div>
                    : room.vertices && room.vertices.length >= 3
                      ? <RoomPlan vertices={room.vertices} height={room.height} walls={room.walls} />
                      : <div className="planvide">Plan non relevé.</div>}
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {/* ── AVANT / APRÈS ── */}
      {afterRooms.map((room, ri) => {
        const before = room.photos.find(p => p.id === room.afterPhoto.fromPhotoId) || room.photos[0];
        return (
          <section className="page" key={ri}>
            <Hdr label="Avant / Après" n={next()} />
            <div className="kicker">Simulation · {nomCourt(room.label)}{room.niveau ? ` · ${room.niveau}` : ''}</div>
            <h2>{nomCourt(room.label)}</h2>
            <div className="ba">
              <figure><img src={before.url} alt="Avant" /><figcaption><span className="badge grey">Aujourd'hui</span></figcaption></figure>
              <figure><img src={room.afterPhoto.url} alt="Après" /><figcaption><span className="badge orange">Après rénovation</span></figcaption></figure>
            </div>
            <div className="ba-cap"><p>{baCaption(room)}{!estProvisoire(room) && Number(room.surface) > 0 ? <span style={{ color: '#94a3b8' }}> · {fmt1(room.surface)} m²</span> : null}</p></div>
          </section>
        );
      })}

      {/* ── MATÉRIAUX ── */}
      {materials.length > 0 && (
        <section className="page">
          <Hdr label="Matériaux &amp; finitions" n={next()} />
          <div className="kicker">Le choix Apex</div>
          <h2>Des finitions durables, faciles d'entretien</h2>
          <div className="matgrid">
            {materials.slice(0, 8).map((m, i) => (
              <div className="matcard" key={i}>
                <div className="matic">{m.icon}</div>
                <div><b>{m.label}</b><span>{m.detail}</span></div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── DEVIS PIÈCE PAR PIÈCE (paginé) ── */}
      {devisPages.map((pagePieces, pi) => (
        <section className="page devispage" key={pi}>
          <Hdr label={`Devis détaillé · ${pi + 1}/${devisPages.length}`} n={next()} />
          <div className="kicker">Détail des prestations · €HT</div>
          <h2>{pi === 0 ? ((data.logements || []).length > 1 ? 'Le détail logement par logement' : 'Le détail pièce par pièce') : 'Suite du devis'}</h2>
          <table className="devis">
            <thead><tr><th>Désignation</th><th className="u">Unité</th><th className="num">Qté</th><th className="num">PU HT</th><th className="num">Total HT</th></tr></thead>
            <tbody>
              {pagePieces.map((p, li) => (
                <React.Fragment key={`${p.id}-${li}`}>
                  {p.entete && !p.suite && <tr className="grp"><td colSpan="4">{p.entete.titre}<span> · {p.entete.sous}</span></td><td className="num">{eur(p.entete.total)}</td></tr>}
                  <tr className="lot"><td colSpan="4">{p.ensemble ? 'Ensemble du bien' : `${p.n ? `${p.n}. ` : ''}${nomCourt(p.label)}`}{p.niveau ? <span style={{ fontWeight: 500, color: '#c2410c' }}> · {p.niveau}</span> : null}{p.surface && !p.provisoire && !p.ensemble && !p.technique && !estExterieur(p) ? <span style={{ fontWeight: 500, color: '#c2410c' }}> · {fmt1(p.surface)} m²</span> : null}{p.suite ? ' (suite)' : ''}</td><td className="num">{p.suite ? '' : eur(p.subtotal)}</td></tr>
                  {p.lines.map((l, i) => (
                    <tr key={i}>
                      <td className="d">{l.label}</td>
                      <td className="u">{l.unit}</td>
                      <td className="num qte">{fmt1(l.qty)}</td>
                      <td className="num pu">{eur(l.pu)}</td>
                      <td className="num tot">{eur(l.total)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
            {pi === devisPages.length - 1 && (
              <tfoot><tr className="grand"><td colSpan="4">Total travaux HT</td><td className="num">{eur(estimate.ht)}</td></tr></tfoot>
            )}
          </table>
          <div className="mini">PU = grille Apex. Quantités estimées sur relevé — à confirmer au métré contradictoire avant devis ferme.</div>
        </section>
      ))}

      {/* ── BUDGET ── */}
      <section className="page">
        <Hdr label="Synthèse · budget" n={next()} />
        <div className="kicker">Investissement · €HT</div>
        <h2>Le budget du projet</h2>
        <div className="budget">
          <div className="bcard"><span className="bk">Travaux HT</span><span className="bv">{eur(estimate.ht)}</span><span className="bu">HT</span></div>
          {/* Sans coordination, le devis ne montre ni la carte ni l'addition. */}
          {estimate.coordination > 0 && <>
            <div className="bplus">+</div>
            <div className="bcard"><span className="bk">Coordination {Math.round((estimate.coordRate ?? 0.03) * 100)} %</span><span className="bv">{eur(estimate.coordination)}</span><span className="bu">HT</span></div>
            <div className="beq">=</div>
            <div className="bcard hl"><span className="bk">Total HT</span><span className="bv">{eur(estimate.htTotal)}</span><span className="bu">HT</span></div>
          </>}
        </div>
        <div className="budget2">
          <div className="b2"><span>Fourchette indicative TTC</span><b>{eur(ttcLow)} → {eur(ttcHigh)}</b></div>
          <div className="b2"><span>TVA ({tvaLabel})</span><b>{eur(estimate.tva)}</b></div>
          <div className="b2 ttc"><span>Total TTC</span><b>{eur(estimate.ttc)}</b></div>
        </div>
        {lots.length > 0 && (
          <div className="lots">
            <div className="lots-t">Répartition par corps de métier</div>
            <div className="lots-g">
              {lots.slice(0, 12).map(l => (
                <div className="lot-r" key={l.cat}><span>{l.label}</span><b>{eur(l.montant)}</b><em>{Math.round(l.part * 100)} %</em></div>
              ))}
            </div>
          </div>
        )}
        <div className="callout" style={{ marginTop: 14 }}><b>À noter</b> — estimation €HT établie sur le relevé. Le montant définitif est arrêté dans le devis contractuel signable. Les rendus « après » sont des projections photoréalistes, sans valeur contractuelle.</div>
      </section>

      {/* ── PROCHAINES ÉTAPES ── */}
      <section className="page">
        <Hdr label="Prochaines étapes" n={next()} />
        <div className="kicker">Et maintenant ?</div>
        <h2>De la proposition au chantier</h2>
        <div className="steps">
          {STEPS.map(s => (
            <div className="step" key={s.n}><span className="sn">{s.n}</span><div><b>{s.t}</b><span>{s.d}</span></div></div>
          ))}
        </div>
        <div className="contact">
          <Wordmark />
          <div className="contact-r">
            <div className="ci"><span>Votre projet</span><b style={{ color: ORANGE, fontSize: '15pt' }}>{eur(estimate.ttc)} TTC</b></div>
            <div className="ci"><span>Estimation valable</span><b>30 jours · €HT</b></div>
          </div>
        </div>
        <div className="finenote">Document de présentation à caractère informatif. Les rendus « après » sont générés à partir des photos du chantier et n'ont pas valeur contractuelle. Le devis définitif signable fait l'objet d'un document séparé. Apex Rénovation — 2 rue du Gué de la Chapelle, 36250 Saint-Maur — contact@apexrenovation.fr.</div>
      </section>
    </div>
  );
}
